import { useCallback, useEffect, useRef, useState } from 'react'
import { Drawer } from 'vaul'
import { Map as MapIcon } from 'lucide-react'
import { useTranslations } from 'use-intl'
import { RestaurantCard } from './RestaurantCard'
import { AreaSelector } from './AreaSelector'

interface IMobileBottomSheetProps {
  restaurants: any[]
  selectedId: number | null
  onSelectRestaurant: (id: number) => void
  onHover?: (id: number | null) => void
  scrollRef?: React.RefObject<HTMLDivElement | null>
  fetchNextPage?: () => void
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  lat?: number
  lng?: number
  onSelectArea?: (lat: number, lng: number) => void
  onSnapStateChange?: (state: MobileResultsSnapState) => void
  isObscured?: boolean
}

const PEEK_SNAP = '96px'
const RESULTS_PREVIEW_SNAP = 0.5
const RESULTS_EXPANDED_SNAP = 1
const SNAP_POINTS = [PEEK_SNAP, RESULTS_PREVIEW_SNAP, RESULTS_EXPANDED_SNAP] as const
export type MobileResultsSnapState = 'peek' | 'preview' | 'expanded'
const SELECTED_CARD_TOP_GAP = 12

function scrollCardIntoPreview(container: HTMLDivElement, card: HTMLElement) {
  const containerRect = container.getBoundingClientRect()
  const cardRect = card.getBoundingClientRect()
  const targetTop = Math.max(
    0,
    container.scrollTop + cardRect.top - containerRect.top - SELECTED_CARD_TOP_GAP,
  )

  if (typeof container.scrollTo === 'function') {
    container.scrollTo({ top: targetTop, behavior: 'smooth' })
    return
  }

  container.scrollTop = targetTop
}

export function MobileBottomSheet({
  restaurants,
  selectedId,
  onSelectRestaurant,
  onHover,
  scrollRef: externalRef,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  lat,
  lng,
  onSelectArea,
  onSnapStateChange,
  isObscured = false,
}: Readonly<IMobileBottomSheetProps>) {
  const t = useTranslations('discovery')
  const internalRef = useRef<HTMLDivElement>(null)
  const scrollRef = externalRef || internalRef

  // Track current snap for conditional rendering
  const [activeSnap, setActiveSnap] = useState<string | number | null>(PEEK_SNAP)
  const [hasActivatedResults, setHasActivatedResults] = useState(false)

  // IntersectionObserver for infinite scroll
  const [observerNode, setObserverNode] = useState<HTMLDivElement | null>(null)
  const observerTargetRef = useCallback((node: HTMLDivElement | null) => {
    setObserverNode(node)
  }, [])

  useEffect(() => {
    if (!observerNode) return
    if (!hasNextPage || !fetchNextPage) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { root: scrollRef.current, rootMargin: '400px', threshold: 0 },
    )

    observer.observe(observerNode)
    return () => observer.disconnect()
  }, [observerNode, hasNextPage, isFetchingNextPage, fetchNextPage, scrollRef])

  // Map-pin selection is a lightweight preview: keep map context visible and reveal the matching card.
  useEffect(() => {
    if (selectedId) {
      setHasActivatedResults(true)
      setActiveSnap(RESULTS_PREVIEW_SNAP)
      setTimeout(() => {
        const el = document.getElementById(`sheet-card-${selectedId}`)
        const container = scrollRef.current
        if (el && container) {
          scrollCardIntoPreview(container, el)
        }
      }, 350)
    }
  }, [scrollRef, selectedId])

  const isFullyCollapsed = activeSnap === PEEK_SNAP
  const isExpanded = activeSnap === RESULTS_EXPANDED_SNAP
  const snapState: MobileResultsSnapState = activeSnap === PEEK_SNAP
    ? 'peek'
    : activeSnap === RESULTS_PREVIEW_SNAP
      ? 'preview'
      : 'expanded'
  const shouldRenderResults = !isFullyCollapsed || hasActivatedResults
  const toggleResults = () => {
    if (activeSnap === PEEK_SNAP) {
      setHasActivatedResults(true)
      setActiveSnap(RESULTS_EXPANDED_SNAP)
    } else {
      setActiveSnap(PEEK_SNAP)
    }
  }

  useEffect(() => {
    onSnapStateChange?.(snapState)
  }, [onSnapStateChange, snapState])

  return (
    <Drawer.Root
      open
      modal={false}
      snapPoints={SNAP_POINTS as unknown as (string | number)[]}
      activeSnapPoint={activeSnap}
      setActiveSnapPoint={setActiveSnap}
      // Dynamic dismissible allows dragging down from higher snap points
      dismissible={activeSnap !== PEEK_SNAP}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          // Prevent actual close by forcing it back to the peek state
          setActiveSnap(PEEK_SNAP)
        }
      }}
    >
      <Drawer.Portal>
        <Drawer.Content
          className={`lg:hidden fixed bottom-0 left-0 right-0 z-30 flex flex-col rounded-t-3xl bg-white/95 dark:bg-stone-950/95 backdrop-blur-xl shadow-[0_-10px_40px_rgba(0,0,0,0.12)] outline-none ${isExpanded ? 'pt-20' : ''} ${isObscured ? 'pointer-events-none' : ''}`}
          aria-hidden={isObscured}
          data-interaction-obscured={isObscured ? 'true' : 'false'}
          data-testid="mobile-results-sheet"
          style={{
            // Vaul snap calculations need a stable height, especially in mobile landscape.
            height: '100dvh',
            maxHeight: '100dvh',
          }}
        >
          {/* Drag Handle */}
          <Drawer.Handle className="mx-auto mt-3 mb-1 h-1.5 w-12 rounded-full bg-stone-300 dark:bg-stone-700 shrink-0" />
          <Drawer.Description className="sr-only">
            {t('drawerDescription', { defaultValue: 'Restaurant results. Expand the sheet to browse the list, or collapse it to return to the map.' })}
          </Drawer.Description>

          {/* Header — always visible in all snap states, with an explicit map return in list mode. */}
          <div className="flex shrink-0 items-center gap-3 px-4 py-2">
            <button
              type="button"
              className="min-w-0 flex-1 rounded-xl px-2 py-1 text-left transition-colors active:bg-stone-100/50 dark:active:bg-stone-800/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label={isFullyCollapsed ? t('viewRestaurants', { n: restaurants.length }) : t('viewMap')}
              onClick={toggleResults}
              data-snap-state={snapState}
              data-testid="mobile-results-toggle"
            >
              <Drawer.Title className="truncate text-lg font-bold font-['Inter'] text-stone-900 dark:text-stone-100 tracking-tight">
                {t('drawerTitle')}
              </Drawer.Title>
              <p className="mt-0.5 truncate text-xs text-stone-500">
                {t('drawerFoundCount', { count: restaurants.length })}
              </p>
            </button>
            {!isFullyCollapsed && (
              <button
                type="button"
                className="flex h-11 shrink-0 items-center gap-2 rounded-full border border-stone-200 bg-white px-4 text-sm font-semibold text-stone-800 shadow-sm transition-colors active:bg-stone-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:border-stone-800 dark:bg-stone-900 dark:text-stone-100 dark:active:bg-stone-800"
                aria-label={t('viewMap')}
                onClick={() => setActiveSnap(PEEK_SNAP)}
                data-testid="mobile-map-return"
              >
                <MapIcon className="h-4 w-4" />
                <span>{t('viewMap')}</span>
              </button>
            )}
          </div>

          {/* Scrollable List — hidden at collapsed snap, visible at half/full */}
          <div
            className={`flex-1 overflow-y-auto no-scrollbar px-4 pb-safe-bottom transition-opacity duration-200 ${
              isFullyCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-100'
            }`}
            ref={scrollRef}
            data-snap-state={snapState}
            data-results-mounted={shouldRenderResults ? 'true' : 'false'}
            data-testid="mobile-results-list"
          >
            {shouldRenderResults && (
              <>
                {onSelectArea && (
                  <div className="mb-4 mt-2">
                    <AreaSelector
                      onSelect={onSelectArea}
                      currentLat={lat}
                      currentLng={lng}
                    />
                  </div>
                )}
                {restaurants.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center mt-4 bg-white dark:bg-stone-800/50 rounded-xl border border-stone-100 dark:border-stone-800 border-dashed">
                    <p className="text-sm font-medium text-stone-500 dark:text-stone-400">
                      {t('sidebar.empty', { defaultValue: 'No restaurants in this area. Try panning the map.' })}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-1">
                    {restaurants.map((restaurant) => (
                      <div
                        key={restaurant.id}
                        id={`sheet-card-${restaurant.id}`}
                        className="[content-visibility:auto] [contain-intrinsic-size:180px]"
                      >
                        <RestaurantCard
                          restaurant={restaurant}
                          isSelected={String(selectedId) === String(restaurant.id)}
                          onPress={() => onSelectRestaurant(restaurant.id)}
                          onHover={onHover}
                        />
                      </div>
                    ))}
                    {hasNextPage && (
                      <div ref={observerTargetRef} className="w-full py-4 flex justify-center">
                        {isFetchingNextPage ? (
                          <div className="w-6 h-6 border-2 border-stone-300 border-t-primary rounded-full animate-spin" />
                        ) : (
                          <div className="h-6" />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
}
